import type { RefObject } from 'react';
import React, { Component, createRef, Suspense } from 'react';

import { Version } from './../ricos-content';
import type { BICallbacks, LegacyViewerPluginConfig, SEOSettings } from './../ricos-types';
import { addDevToolsClickListener } from './../wix-rich-content-common';
import { RicosDevToolsLazy, shouldShowDebuggingInfo } from './../wix-rich-content-common/lib/devTools';
import { getContentSummary } from './../wix-rich-content-common/lib/ricosContentAnalytics';

import { ContentViewer } from './ContentViewer';
import { FullscreenProvider } from './modals/fullscreen/fullscreen-provider';
import type { RicosViewerProps } from './types';
import { hasFullscreen, mergeViewerProps } from './utils';

interface State {
  error?: string;
  TextSelectionToolbar?: $TSFixMe;
  LinkPreviewPopover?: $TSFixMe;
  debugInvokedWithMouse?: boolean;
}

class RicosViewerNext extends Component<RicosViewerProps, State> {
  viewerRef!: RefObject<any>;

  getBiCallback: <T extends keyof BICallbacks>(key: T) => BICallbacks[T] | undefined;

  static displayName = 'RicosViewerNext';

  static getDerivedStateFromError(error: string) {
    return { error };
  }

  componentDidCatch(error: $TSFixMe, errorInfo: $TSFixMe) {
    console.error({ error, errorInfo });
  }

  constructor(props: RicosViewerProps) {
    super(props);
    this.getBiCallback = key => {
      const { children, _rcProps } = this.props;
      return children?.props.helpers?.[key] || _rcProps?.helpers?.[key];
    };
    this.viewerRef = createRef();
    this.state = {};
  }

  static defaultProps = {
    onError: (err: $TSFixMe) => {
      throw err;
    },
  };

  componentDidMount() {
    const onViewerLoaded = this.getBiCallback('onViewerLoaded');
    const content = this.props.content || this.props._rcProps?.content;
    const { pluginsCount } = (content && getContentSummary(content)) || {};
    pluginsCount &&
      onViewerLoaded?.({
        isPreview: false,
        version: Version.currentVersion,
        pluginsCount,
        url: document.URL,
        contentId: content?.metadata?.id,
      });

    addDevToolsClickListener(this.viewerRef.current, () =>
      this.setState({ debugInvokedWithMouse: true })
    );
  }

  loadTextSelection = () => {
    const { textSelectionToolbar, isMobile } = this.props;
    const needTextSelectionToolbar = textSelectionToolbar && !isMobile;
    if (needTextSelectionToolbar && !this.state.TextSelectionToolbar) {
      const TextSelectionToolbar = React.lazy(() => import('./TextSelectionToolbar'));
      this.setState({ TextSelectionToolbar });
    }
  };

  loadLinkPreviewPopover = () => {
    const { linkPreviewPopoverFetchData, isMobile } = this.props;
    const needLoadLinkPreviewPopover = linkPreviewPopoverFetchData && !isMobile;
    if (needLoadLinkPreviewPopover && !this.state.LinkPreviewPopover) {
      const LinkPreviewPopover = React.lazy(() => import('./LinkPreviewPopover'));
      this.setState({ LinkPreviewPopover });
    }
  };

  onMouseOver = () => {
    this.loadTextSelection();
    this.loadLinkPreviewPopover();
  };

  render() {
    const {
      seoSettings,
      linkPreviewPopoverFetchData,
      mediaSettings,
      isMobile,
      content,
      experiments,
      debugMode,
    } = this.props;
    const { TextSelectionToolbar, LinkPreviewPopover } = this.state;
    const mergedProps = mergeViewerProps({
      ...this.props,
      viewerProps: {
        seoMode: seoSettings as SEOSettings | undefined,
        onMouseOver: this.onMouseOver,
        locale: this.props.locale,
      },
    });

    const contentViewer = ({
      config,
      onHover,
    }: {
      config: LegacyViewerPluginConfig;
      onHover: () => void;
    }) => (
      <ContentViewer
        {...mergedProps}
        config={config}
        onHover={onHover}
        key="viewer"
        setRef={this.viewerRef}
        devTools={
          this.state.debugInvokedWithMouse ||
          shouldShowDebuggingInfo(debugMode?.includes('all')) ? (
            <React.Suspense>
              <RicosDevToolsLazy
                version={Version.currentVersion}
                mode="viewer"
                contentState={content}
                ricosProps={this.props}
                autoOpen={this.state.debugInvokedWithMouse ?? false}
              />
            </React.Suspense>
          ) : null
        }
      />
    );
    const contentViewerWrapped = hasFullscreen(mergedProps.config) ? (
      <FullscreenProvider
        key="RicosFullscreenProvider"
        fullscreenProps={mediaSettings?.fullscreenProps}
        isMobile={!!isMobile}
        content={content}
        experiments={experiments}
        config={mergedProps.config}
        jsdomMode={mergedProps.jsdomMode}
      >
        {contentViewer}
      </FullscreenProvider>
    ) : (
      contentViewer({ config: mergedProps.config, onHover: () => {} })
    );

    try {
      if (this.state.error) {
        this.props.onError?.(this.state.error);
        return null;
      }
      return [
        contentViewerWrapped,
        TextSelectionToolbar ? (
          <Suspense key="TextSelectionToolbar" fallback={<div />}>
            <TextSelectionToolbar
              onButtonClick={this.getBiCallback('onViewerAction')}
              container={this.viewerRef.current}
            />
          </Suspense>
        ) : null,
        LinkPreviewPopover ? (
          <Suspense key="LinkPreviewPopover" fallback={<div />}>
            <LinkPreviewPopover
              fetchUrlPreviewData={linkPreviewPopoverFetchData}
              container={this.viewerRef.current}
            />
          </Suspense>
        ) : null,
      ];
    } catch (e) {
      // @ts-ignore $TSFixMe
      this.props.onError?.(e);
      return null;
    }
  }
}

export default RicosViewerNext;
